import React, { useEffect, useMemo } from "react";
import { useSearch } from "../../../contexts/SearchContext";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import FlightDetailsWave1bLive from "../../../features/flightEngineV2/details/FlightDetailsWave1bLive";
import { sumCheckedRawSupplementFares } from "../adapters/flightOfferAdapter";
import {
  computeSelfComposePricingSsot,
  legAdultFare,
  overlayEngineClientOnSelfComposeSsot,
} from "./selfCompose/selfComposeSummaryUtils";
import { useBookingFlow } from "./hooks/useBookingFlow";

function isSelfComposeOffer(offer) {
  return !!(
    offer?.isSelfComposeCombined ||
    offer?.source === "selfCompose"
  );
}

function resolveSelfComposeDeals(offer, outCtx, inCtx) {
  const out = outCtx ?? offer?.selfComposeLegs?.outboundRawDeal ?? null;
  const inn = inCtx ?? offer?.selfComposeLegs?.inboundRawDeal ?? null;
  return { out, inn };
}

export default function FlightDetailsStepCard() {
  const {
    selectedFlightOffer,
    passengers,
  } = useBookingFlow();
  const {
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
    roomInfo,
  } = useSearch();
  const { ldsSupplement } = useSelectedResult();
  const baggageRaw = useMemo(
    () => sumCheckedRawSupplementFares(ldsSupplement),
    [ldsSupplement]
  );

  const selfComposePricing = useMemo(() => {
    if (!selectedFlightOffer || !isSelfComposeOffer(selectedFlightOffer)) {
      return null;
    }
    const { out, inn } = resolveSelfComposeDeals(
      selectedFlightOffer,
      selectedSelfComposeOutboundDeal,
      selectedSelfComposeInboundDeal
    );
    if (!out || !inn) return null;
    const rawSsot = computeSelfComposePricingSsot(
      out,
      inn,
      roomInfo,
      baggageRaw,
      passengers.length
    );
    const ssot = overlayEngineClientOnSelfComposeSsot(rawSsot, selectedFlightOffer);
    if (!ssot) return null;
    const outboundPrice = legAdultFare(out);
    const inboundPrice = legAdultFare(inn);
    return {
      outboundPrice,
      inboundPrice,
      totalPerPerson: ssot.totalPerPerson,
      totalBooking: ssot.totalBooking,
      currency: ssot.currency,
      paxCount: ssot.passengerCount,
    };
  }, [
    selectedFlightOffer,
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
    roomInfo,
    passengers.length,
    baggageRaw,
  ]);

  useEffect(() => {
    const scrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    scrollTop();
    const id = requestAnimationFrame(scrollTop);
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!selectedFlightOffer) return;
    if (selfComposePricing) {
      console.log("FlightDetails pricing", {
        mode: "selfCompose",
        totalBooking: selfComposePricing.totalBooking,
        totalPerPerson: selfComposePricing.totalPerPerson,
      });
    } else {
      console.log("FlightDetails pricing", { mode: "classic" });
    }
  }, [selectedFlightOffer, selfComposePricing]);

  return <FlightDetailsWave1bLive />;
}
